import { z } from 'zod';
import { name, uuid } from './fields';
import { AiSuggestionModel } from './master-list-items';
import { CreateTripItemInput, TripItemWithLabels } from './trip-items';

/**
 * AI Quick Add — the user dictates/types a freeform instruction ("add sunscreen
 * and two beach umbrellas to beach day, and move the mixer to sound equipment")
 * and the model interprets it against the trip's current lists + items into
 * discrete proposals — new items routed to a target list (duplicates flagged),
 * or existing items to move between lists. The interpret call is READ-ONLY; the
 * user reviews/edits, then commits via bulk add and/or move.
 *
 * Quantities collapse into the name (e.g. "Beach umbrella ×2") — trip items have
 * no quantity column. A single command can target multiple different lists.
 */

/** POST /api/trips/:tripId/quick-add body. */
export const QuickAddInput = z.object({
  instruction: z.string().trim().min(1).max(1000),
  model: AiSuggestionModel.default('claude_fast'),
});
export type QuickAddInput = z.infer<typeof QuickAddInput>;

/** Upper bound on one voice clip (base64 chars). 16 kHz mono PCM16 WAV is
 * 32 KB/s, so this is roughly two minutes of speech — far past a packing ask. */
export const QUICK_ADD_AUDIO_MAX_BASE64 = 6_000_000;

/**
 * POST /api/trips/:tripId/quick-add/voice body — a spoken instruction. The
 * browser records, re-encodes to 16 kHz mono WAV, and sends it base64; the
 * server hands the audio straight to an audio-capable model, which transcribes
 * AND routes items in one call. The model is pinned server-side (not every
 * chat model accepts audio), so there's no `model` field here.
 */
export const QuickAddVoiceInput = z.object({
  audioBase64: z.string().min(1).max(QUICK_ADD_AUDIO_MAX_BASE64),
});
export type QuickAddVoiceInput = z.infer<typeof QuickAddVoiceInput>;

/**
 * tripListKey of a target list — an origin master-list id or `local:<name>` for
 * a trip-local list. Null = "Manually added items".
 */
const targetListKey = () => z.string().nullable();

/** One change the AI proposes: add a new item, or move an existing one. */
export const QuickAddProposal = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('add'),
    name: name(),
    description: z.string().nullable(),
    targetListKey: targetListKey(),
    /** Snapshot name of the target list (null when targetListKey is null). */
    targetListName: z.string().nullable(),
    /** Already present in the target list (server-recomputed; default it unchecked). */
    isDuplicate: z.boolean(),
    /** If it instead matches an item in a *different* list, that list's name. */
    duplicateElsewhere: z.string().nullable(),
  }),
  z.object({
    action: z.literal('move'),
    /** The existing trip item to move (resolved server-side; never model-invented). */
    itemId: uuid(),
    /** The item's current name, for display. */
    name: name(),
    /** Where it lives now (null key = the manual bucket). */
    sourceListKey: z.string().nullable(),
    sourceListName: z.string().nullable(),
    targetListKey: targetListKey(),
    targetListName: z.string().nullable(),
  }),
]);
export type QuickAddProposal = z.infer<typeof QuickAddProposal>;

/** POST /api/trips/:tripId/quick-add response. */
export const QuickAddResult = z.object({
  proposals: z.array(QuickAddProposal),
  /** Optional short note from the model (e.g. when nothing actionable was found). */
  note: z.string().nullable(),
  /** What the model heard (voice path only; null for typed instructions). */
  transcript: z.string().nullable(),
});
export type QuickAddResult = z.infer<typeof QuickAddResult>;

/**
 * POST /api/trips/:tripId/items/bulk body — append several items in one atomic
 * transaction. Each entry reuses the single-add shape; `originMasterListId` slots
 * it into an existing list (server snapshots the name; unknown origin → manual).
 */
export const BulkCreateTripItemsInput = z.object({
  items: z.array(CreateTripItemInput).min(1).max(50),
});
export type BulkCreateTripItemsInput = z.infer<typeof BulkCreateTripItemsInput>;

/** POST /api/trips/:tripId/items/bulk response — the created items. */
export const BulkCreateTripItemsOutput = z.object({
  items: z.array(TripItemWithLabels),
});
export type BulkCreateTripItemsOutput = z.infer<typeof BulkCreateTripItemsOutput>;
